import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../core/localization/localization.service';
import { WayfindingService } from '../core/wayfinding/wayfinding.service';
import { WayfindingRecentsService } from '../core/wayfinding/wayfinding-recents.service';
import { WayfindingPlay, WayfindingRecent, WayfindingSearchResult } from '../core/wayfinding/wayfinding.models';
import { InfoHintComponent } from '../shared/design-system/info-hint.component';
import { AinPageComponent } from '../shared/design-system/page.component';

@Component({
  standalone: true,
  selector: 'ain-dashboard',
  imports: [AinPageComponent, InfoHintComponent, RouterLink, ButtonModule, InputTextModule, TagModule],
  template: `
    <ain-page [eyebrow]="t('wayfinding.eyebrow')" [title]="t('wayfinding.title')" [description]="t('wayfinding.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'wayfinding.hint.title'"
        [descriptionKey]="'wayfinding.hint.description'"
        [bulletKeys]="['wayfinding.hint.bullets.path', 'wayfinding.hint.bullets.context', 'wayfinding.hint.bullets.workflow', 'wayfinding.hint.bullets.rag']" />

      <form class="ask" (submit)="runSearch($event)">
        <i class="pi pi-sparkles ask-icon" aria-hidden="true"></i>
        <input
          id="wayfinding-ask"
          pInputText
          [value]="query()"
          [attr.aria-label]="t('wayfinding.askPlaceholder')"
          [placeholder]="t('wayfinding.askPlaceholder')"
          (input)="query.set($any($event.target).value)" />
        <button pButton type="submit" icon="pi pi-directions" [label]="t('wayfinding.search')"></button>
      </form>

      @if (recents.recents().length > 0 && !result()?.query) {
        <section class="block recents-block">
          <h3>{{ t('wayfinding.recents.title') }}</h3>
          <div class="chips">
            @for (recent of recents.recents(); track recent.route) {
              <a class="chip" [routerLink]="recent.route">
                <i class="pi pi-history" aria-hidden="true"></i>
                {{ recentLabel(recent) }}
              </a>
            }
          </div>
        </section>
      }

      <nav class="shortcuts" aria-label="Quick actions">
        <a routerLink="/workflow/tasks" class="shortcut">
          <i class="pi pi-inbox" aria-hidden="true"></i>
          <span>{{ t('wayfinding.shortcuts.tasks') }}</span>
        </a>
        <a routerLink="/workflow/intents/new" class="shortcut">
          <i class="pi pi-bolt" aria-hidden="true"></i>
          <span>{{ t('wayfinding.shortcuts.newIntent') }}</span>
        </a>
        <a routerLink="/workflow/intents/approvals/inbox" class="shortcut">
          <i class="pi pi-verified" aria-hidden="true"></i>
          <span>{{ t('wayfinding.shortcuts.approvals') }}</span>
        </a>
      </nav>

      @if (result(); as found) {
        @if (found.query || found.continued || found.plays.length || found.jobs.length || found.screens.length || found.workflows.length) {
          <section class="block">
            <h3>{{ t(found.workflowPreferred ? 'wayfinding.workflowMatch.title' : found.continued ? 'wayfinding.here.next' : 'wayfinding.results.title') }}</h3>
            @if (found.plays.length === 0 && found.jobs.length === 0 && found.screens.length === 0 && found.workflows.length === 0) {
              <p class="empty">{{ t('wayfinding.results.empty') }}</p>
            }

            @if (found.here) {
              <p class="here-line">
                <i class="pi pi-map-marker" aria-hidden="true"></i>
                {{ t('wayfinding.here.title') }}: {{ found.here.label }}
                @if (found.continued) {
                  <span class="here-next">{{ t('wayfinding.here.next') }}</span>
                }
              </p>
            }

            @if (found.workflowPreferred) {
              @for (workflow of found.workflows; track workflow.id) {
                <article class="journey workflow-hit">
                  <header class="journey-head">
                    <span class="journey-icon" aria-hidden="true"><i class="pi pi-sitemap"></i></span>
                    <div class="journey-copy">
                      <h4>{{ workflow.name }}</h4>
                      <p>{{ t('wayfinding.workflowMatch.description') }}</p>
                    </div>
                    <p-tag [value]="t('wayfinding.workflowMatch.title')" severity="success" />
                    <a pButton [routerLink]="workflow.route" icon="pi pi-sitemap" [label]="t('wayfinding.workflowMatch.open')"></a>
                  </header>
                  <div class="journey-actions">
                    <a pButton severity="help" [outlined]="true" icon="pi pi-bolt" routerLink="/workflow/intents/new" [queryParams]="{ q: found.query }" [label]="t('wayfinding.startIntent')"></a>
                  </div>
                </article>
              }

              @if (found.plays.length > 0) {
                <h3>{{ t('wayfinding.orScreens') }}</h3>
              }
            }

            @for (play of found.plays; track play.id) {
              <article class="journey">
                <header class="journey-head">
                  <span class="journey-icon" aria-hidden="true"><i class="pi" [class]="playIcon(play.id)"></i></span>
                  <div class="journey-copy">
                    <h4>{{ t(play.titleKey) }}</h4>
                    <p>{{ t(play.descriptionKey) }}</p>
                  </div>
                  @if (found.continued) {
                    <p-tag [value]="t('wayfinding.continuePath')" severity="success" />
                  }
                  <p-tag [value]="stepCount(play)" severity="info" />
                  <a
                    pButton
                    [routerLink]="startRoute(play)"
                    icon="pi pi-play"
                    [label]="t(found.continued ? 'wayfinding.continuePath' : 'wayfinding.startPath')"></a>
                </header>

                <ol class="path">
                  @for (step of requiredSteps(play); track step.route; let idx = $index; let last = $last) {
                    <li>
                      <a class="step" [routerLink]="step.route">
                        <span class="step-index">{{ idx + 1 }}</span>
                        <i class="pi step-glyph" [class]="stepIcon(step.route)" aria-hidden="true"></i>
                        <strong>{{ t(step.titleKey) }}</strong>
                        <small>{{ t(step.whyKey) }}</small>
                      </a>
                    </li>
                    @if (!last) {
                      <li class="path-join" aria-hidden="true">
                        <i class="pi" [class]="isRtl() ? 'pi-arrow-left' : 'pi-arrow-right'"></i>
                      </li>
                    }
                  }
                </ol>
                @if (optionalSteps(play).length > 0) {
                  <p class="optional-label">{{ t('wayfinding.optionalSteps') }}</p>
                  <ol class="path optional-path">
                    @for (step of optionalSteps(play); track step.route) {
                      <li>
                        <a class="step optional" [routerLink]="step.route">
                          <span class="step-index">+</span>
                          <i class="pi step-glyph" [class]="stepIcon(step.route)" aria-hidden="true"></i>
                          <strong>{{ t(step.titleKey) }}</strong>
                          <small>{{ t(step.whyKey) }}</small>
                          <span class="optional-tag">{{ t('wayfinding.optionalStep') }}</span>
                        </a>
                      </li>
                    }
                  </ol>
                }

                <div class="journey-actions">
                  @if (play.workflowRoute) {
                    <a pButton severity="secondary" [outlined]="true" icon="pi pi-sitemap" [routerLink]="play.workflowRoute" [label]="t('wayfinding.openWorkflow')"></a>
                  }
                  @if (play.intentKey) {
                    <a pButton severity="help" [outlined]="true" icon="pi pi-bolt" routerLink="/workflow/intents/new" [queryParams]="{ q: found.query }" [label]="t('wayfinding.startIntent')"></a>
                  }
                </div>
              </article>
            }

            @if (found.jobs.length > 0) {
              <div class="action-grid">
                @for (job of found.jobs; track job.id) {
                  <a class="action-tile" [routerLink]="job.route">
                    <span class="action-icon" aria-hidden="true"><i class="pi" [class]="jobIcon(job.id)"></i></span>
                    <span class="action-copy">
                      <strong>{{ t(job.titleKey) }}</strong>
                      <small>{{ t(job.descriptionKey) }}</small>
                    </span>
                    <span class="action-go">
                      {{ t('wayfinding.go') }}
                      <i class="pi" [class]="isRtl() ? 'pi-arrow-left' : 'pi-arrow-right'" aria-hidden="true"></i>
                    </span>
                  </a>
                }
              </div>
            }

            @if (found.screens.length > 0) {
              <div>
                <h4 class="related-title">{{ t('wayfinding.relatedScreens') }}</h4>
                <div class="chips">
                  @for (screen of found.screens; track screen.route) {
                    <a class="chip" [routerLink]="screen.route">
                      <i class="pi" [class]="stepIcon(screen.route)" aria-hidden="true"></i>
                      {{ screen.label }}
                    </a>
                  }
                </div>
              </div>
            }

            @if (!found.workflowPreferred) {
              @for (workflow of found.workflows; track workflow.id) {
                <article class="journey workflow-hit">
                  <header class="journey-head">
                    <span class="journey-icon" aria-hidden="true"><i class="pi pi-sitemap"></i></span>
                    <div class="journey-copy">
                      <h4>{{ workflow.name }}</h4>
                      <p>{{ t('wayfinding.workflowMatch.description') }}</p>
                    </div>
                    <p-tag [value]="t('wayfinding.workflowMatch.title')" severity="success" />
                    <a pButton [routerLink]="workflow.route" icon="pi pi-sitemap" [label]="t('wayfinding.workflowMatch.open')"></a>
                  </header>
                  <div class="journey-actions">
                    <a pButton severity="help" [outlined]="true" icon="pi pi-bolt" routerLink="/workflow/intents/new" [queryParams]="{ q: found.query }" [label]="t('wayfinding.startIntent')"></a>
                  </div>
                </article>
              }
            }
          </section>
        }
      }

      <section class="block">
        <h3>{{ t('wayfinding.suggested.title') }}</h3>
        <div class="play-grid">
          @for (play of wayfinding.suggestedPlays(); track play.id) {
            <button type="button" class="play-tile" (click)="showPlay(play)">
              <span class="play-tile-icon" aria-hidden="true"><i class="pi" [class]="playIcon(play.id)"></i></span>
              <strong>{{ t(play.titleKey) }}</strong>
              <div class="mini-path" aria-hidden="true">
                @for (step of requiredSteps(play); track step.route; let last = $last; let idx = $index) {
                  <span class="mini-node">{{ idx + 1 }}</span>
                  @if (!last) {
                    <span class="mini-line"></span>
                  }
                }
              </div>
              <span class="play-tile-cta">
                {{ t('wayfinding.startPath') }}
                <i class="pi" [class]="isRtl() ? 'pi-arrow-left' : 'pi-arrow-right'"></i>
              </span>
            </button>
          }
        </div>
      </section>
    </ain-page>
  `,
  styles: [`
    .ask {
      display: flex;
      gap: 10px;
      align-items: center;
      margin-bottom: 16px;
      padding: 10px 12px;
      border: 1px solid #c7d2fe;
      border-radius: 16px;
      background: linear-gradient(180deg, #eef2ff, #ffffff);
    }
    .ask-icon { color: #4f46e5; font-size: 1.1rem; }
    .ask input { flex: 1 1 auto; border: 0 !important; box-shadow: none !important; background: transparent !important; }
    .shortcuts { display: flex; gap: 8px; flex-wrap: wrap; margin: 0 0 24px; }
    .shortcut {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border-radius: 999px;
      background: #f1f5f9;
      color: #0f172a;
      text-decoration: none;
      font-weight: 600;
      font-size: 13px;
    }
    .shortcut:hover { background: #e0e7ff; color: #3730a3; }
    .block { display: grid; gap: 16px; margin-bottom: 28px; }
    .block h3 { margin: 0; font-size: 1.05rem; }
    .empty { color: #64748b; }
    .here-line {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      margin: 0;
      color: #4338ca;
      font-weight: 600;
      font-size: 13px;
    }
    .here-next { color: #64748b; font-weight: 600; }
    .recents-block { margin-bottom: 16px; }
    .workflow-hit {
      border-color: #86efac;
      background: linear-gradient(160deg, #ecfdf5 0%, #ffffff 42%);
    }
    .journey {
      display: grid;
      gap: 16px;
      padding: 18px;
      border-radius: 18px;
      border: 1px solid #c7d2fe;
      background: linear-gradient(160deg, #eef2ff 0%, #ffffff 42%);
      box-shadow: 0 10px 28px rgb(79 70 229 / 8%);
    }
    .journey-head { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
    .journey-icon, .play-tile-icon, .action-icon {
      width: 44px;
      height: 44px;
      border-radius: 14px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #4f46e5;
      color: white;
      font-size: 1.1rem;
      flex: 0 0 auto;
    }
    .journey-copy { flex: 1 1 220px; min-width: 0; }
    .journey-copy h4, .action-copy strong, .play-tile strong { margin: 0; }
    .journey-copy p { margin: 4px 0 0; color: #64748b; }
    .path {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: stretch;
      gap: 8px;
    }
    .path li { display: flex; align-items: stretch; }
    .path-join { align-items: center !important; color: #6366f1; padding-inline: 2px; }
    .step {
      display: grid;
      justify-items: start;
      gap: 6px;
      min-width: 148px;
      max-width: 200px;
      padding: 12px;
      border-radius: 14px;
      background: white;
      border: 1px solid #e0e7ff;
      text-decoration: none;
      color: inherit;
      box-shadow: 0 6px 16px rgb(15 23 42 / 6%);
    }
    .step:hover { border-color: #4f46e5; transform: translateY(-2px); }
    .step-index {
      width: 22px;
      height: 22px;
      border-radius: 999px;
      background: #4f46e5;
      color: white;
      font-size: 12px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .step-glyph { color: #4f46e5; }
    .step strong { font-size: 13px; }
    .step small { color: #64748b; line-height: 1.35; }
    .step.optional { border-style: dashed; background: #f8fafc; }
    .optional-label { margin: 8px 0 0; font-size: 13px; color: #64748b; font-weight: 600; }
    .optional-tag { font-size: 11px; color: #6366f1; font-weight: 700; }
    .optional-path { margin-top: 4px; }
    .journey-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .action-grid, .play-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
    .action-tile, .play-tile {
      display: grid;
      gap: 10px;
      padding: 16px;
      border-radius: 16px;
      border: 1px solid #e2e8f0;
      background: white;
      text-align: start;
      text-decoration: none;
      color: inherit;
      cursor: pointer;
      box-shadow: 0 8px 20px rgb(15 23 42 / 5%);
    }
    .action-tile { grid-template-columns: auto 1fr auto; align-items: center; }
    .action-tile:hover, .play-tile:hover { border-color: #4f46e5; box-shadow: 0 12px 28px rgb(79 70 229 / 12%); }
    .action-copy { display: grid; gap: 4px; }
    .action-copy small { color: #64748b; }
    .action-go, .play-tile-cta {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: #4f46e5;
      font-weight: 700;
      font-size: 13px;
    }
    .play-tile-icon { background: #4338ca; }
    .mini-path { display: flex; align-items: center; gap: 6px; }
    .mini-node {
      width: 22px;
      height: 22px;
      border-radius: 999px;
      background: #e0e7ff;
      color: #3730a3;
      font-size: 11px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .mini-line { flex: 1 1 16px; height: 3px; border-radius: 99px; background: linear-gradient(90deg, #c7d2fe, #818cf8); }
    .related-title { margin: 0 0 8px; font-size: 13px; color: #64748b; }
    .chips { display: flex; flex-wrap: wrap; gap: 8px; }
    .chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      border-radius: 999px;
      background: #eef2ff;
      color: #312e81;
      text-decoration: none;
      font-weight: 600;
      font-size: 13px;
    }
    .chip:hover { background: #4f46e5; color: white; }
  `]
})
export class DashboardComponent implements OnInit {
  private readonly localizationService = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly wayfinding = inject(WayfindingService);
  readonly recents = inject(WayfindingRecentsService);
  readonly isRtl = computed(() => {
    const culture = this.localizationService.activeCulture().toLowerCase();
    return culture.startsWith('fa') || culture.startsWith('ar');
  });

  readonly query = signal('');
  readonly fromRoute = signal('');
  readonly result = signal<WayfindingSearchResult | null>(null);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pinnedPlay = signal<WayfindingPlay | null>(null);

  constructor() {
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const q = params.get('q') ?? '';
      const from = params.get('from') ?? '';
      const pinned = this.pinnedPlay();
      if (pinned && q !== this.t(pinned.titleKey)) {
        this.pinnedPlay.set(null);
      }
      this.query.set(q);
      this.fromRoute.set(from);
      if (q.trim() || from.trim()) {
        this.applySearch(q, from);
      } else {
        this.pinnedPlay.set(null);
        this.result.set(null);
      }
    });
  }

  ngOnInit(): void {
    this.wayfinding.ensureCorpusSynced().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.wayfinding.ensureDefinitionsLoaded().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const q = this.query();
      const from = this.fromRoute();
      if (q.trim() || from.trim()) {
        this.applySearch(q, from);
      }
    });
    const q = this.query();
    const from = this.fromRoute();
    if (q.trim() || from.trim()) {
      this.applySearch(q, from);
    }
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  requiredSteps(play: WayfindingPlay) {
    return play.steps.filter(step => !step.optional);
  }

  optionalSteps(play: WayfindingPlay) {
    return play.steps.filter(step => step.optional);
  }

  startRoute(play: WayfindingPlay): string {
    return this.requiredSteps(play)[0]?.route ?? play.steps[0]?.route ?? '/dashboard';
  }

  stepCount(play: WayfindingPlay): string {
    const required = this.requiredSteps(play).length;
    const optional = this.optionalSteps(play).length;
    if (optional === 0) {
      return `${play.steps.length} ${this.t('wayfinding.stepsLabel')}`;
    }

    return `${required} ${this.t('wayfinding.stepsLabel')} + ${optional} ${this.t('wayfinding.optionalStep')}`;
  }

  runSearch(event?: Event): void {
    event?.preventDefault();
    this.pinnedPlay.set(null);
    const q = this.query().trim();
    const from = this.fromRoute().trim();
    void this.router.navigate(['/dashboard'], { queryParams: { ...(q ? { q } : {}), ...(from ? { from } : {}) } });
    this.applySearch(q, from);
  }

  showPlay(play: WayfindingPlay): void {
    const label = this.t(play.titleKey);
    this.pinnedPlay.set(play);
    this.query.set(label);
    void this.router.navigate(['/dashboard'], { queryParams: { q: label } });
    this.applySearch(label, this.fromRoute());
  }

  recentLabel(recent: WayfindingRecent): string {
    if (recent.labelKey) {
      const translated = this.t(recent.labelKey);
      if (translated && translated !== recent.labelKey) {
        return translated;
      }
    }

    return recent.fallbackLabel || recent.route;
  }

  playIcon(id: string): string {
    const icons: Record<string, string> = {
      'play-ai-article': 'pi-pencil',
      'play-ace-article': 'pi-sparkles',
      'play-lead-to-cash': 'pi-wallet',
      'play-sell-and-collect': 'pi-shopping-cart',
      'play-onboard-party': 'pi-user-plus',
      'play-issue-policy': 'pi-shield',
      'play-grow-lead': 'pi-megaphone',
      'play-approve-work': 'pi-verified'
    };
    return icons[id] ?? 'pi-compass';
  }

  jobIcon(id: string): string {
    const icons: Record<string, string> = {
      'job-sales-orders': 'pi-shopping-bag',
      'job-quotes': 'pi-file-edit',
      'job-catalog-offerings': 'pi-box',
      'job-parties': 'pi-users',
      'job-leads': 'pi-user',
      'job-invoices': 'pi-book',
      'job-receipts': 'pi-wallet',
      'job-tasks': 'pi-inbox',
      'job-intents': 'pi-bolt',
      'job-policies': 'pi-file',
      'job-journal': 'pi-book',
      'job-ace-brief': 'pi-file-edit',
      'job-ace-studio': 'pi-sparkles',
      'job-article-publishing': 'pi-file-edit',
      'job-content-assets': 'pi-images',
      'job-publish-operations': 'pi-send'
    };
    return icons[id] ?? 'pi-arrow-circle-right';
  }

  stepIcon(route: string): string {
    if (route.includes('ace-studio')) {
      return 'pi-sparkles';
    }
    if (route.includes('content-assets')) {
      return 'pi-images';
    }
    if (route.includes('article-publishing')) {
      return 'pi-file-edit';
    }
    if (route.includes('publish-operations')) {
      return 'pi-send';
    }
    if (route.includes('catalog') || route.includes('offering')) {
      return 'pi-box';
    }
    if (route.includes('sales')) {
      return 'pi-shopping-cart';
    }
    if (route.includes('financial') || route.includes('payment')) {
      return 'pi-wallet';
    }
    if (route.includes('insurance')) {
      return 'pi-shield';
    }
    if (route.includes('growth')) {
      return 'pi-chart-line';
    }
    if (route.includes('identity')) {
      return 'pi-lock';
    }
    if (route.includes('workflow')) {
      return 'pi-sitemap';
    }
    if (route.includes('party')) {
      return 'pi-id-card';
    }
    return 'pi-window-maximize';
  }

  private applySearch(q: string, from = this.fromRoute()): void {
    const run = (query: string, route: string) => {
      if (!query.trim() && !route.trim()) {
        this.result.set(null);
        return;
      }

      const found = this.wayfinding.search(query, {
        currentRoute: route,
        recents: this.recents.recents()
      });
      const pinned = this.pinnedPlay();
      if (pinned && query === this.t(pinned.titleKey)) {
        this.result.set({
          ...found,
          continued: false,
          workflowPreferred: false,
          workflows: [],
          plays: [{ ...pinned, score: 100 }],
          jobs: [],
          screens: [],
          knowledge: []
        });
        return;
      }

      this.result.set(found);
    };
    run(q, from);
    this.wayfinding.ensureDefinitionsLoaded().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(loaded => {
      if (!loaded) {
        return;
      }

      if (this.query() !== q || this.fromRoute() !== from) {
        return;
      }

      run(this.query(), this.fromRoute());
    });
  }
}
